// ============================================================
// ClientAccessGate — access gate for Client Connect (/connect/*).
//
// This is SEPARATE from the operator AccessGate and must stay that way:
//   - AccessGate (auth/AccessGate.tsx) gates /square/* for OPERATORS
//     (GitHub auth + allowlist). It is unchanged by this file.
//   - ClientAccessGate gates /connect/* for CLIENTS via project
//     membership. It NEVER widens operator access and never imports or
//     reuses AccessGate.
//
// FAIL CLOSED. A client only reaches a project workspace when they have
// an ACTIVE membership for that project. Any other state — missing,
// inactive, revoked, or "not yet implemented" — renders a polished,
// client-facing closed screen and never mounts the workspace.
//
// Phase 1: the membership/session resolution backend does not exist yet,
// so useClientMembership() returns 'not_implemented' and the gate is
// closed for everyone. This is intentional: Client Connect is not live.
// Phase 2 wires real resolution here without touching the operator gate.
// ============================================================

import type { ReactNode } from 'react'
import { useParams } from 'react-router-dom'

export type ClientMembershipState =
  | 'not_implemented'
  | 'missing'
  | 'inactive'
  | 'revoked'
  | 'active'

/**
 * Resolve the current visitor's membership for a Client Connect project.
 *
 * Phase 1 foundation: there is no client membership/session resolution
 * yet, so this always reports 'not_implemented' (fail closed). It is a
 * stub with a stable shape so Phase 2 can replace the body with a real,
 * RLS-backed lookup without changing the gate's contract. It performs no
 * network calls and reads no operator state.
 */
export function useClientMembership(_projectSlug: string | undefined): {
  state: ClientMembershipState
} {
  return { state: 'not_implemented' }
}

const COPY: Record<Exclude<ClientMembershipState, 'active'>, { title: string; body: string }> = {
  not_implemented: {
    title: 'This workspace is being prepared.',
    body: 'Your project space isn’t open yet. You’ll be able to sign in here once it’s ready.',
  },
  missing: {
    title: 'Your project access is not active yet.',
    body: 'We couldn’t find an active invitation for this workspace. Your project contact can send you an invite.',
  },
  inactive: {
    title: 'Your project access is not active yet.',
    body: 'Your access to this workspace is paused. Reach out to your project contact to reactivate it.',
  },
  revoked: {
    title: 'This workspace is no longer available to you.',
    body: 'Your access to this project has ended. If you think this is a mistake, contact your project lead.',
  },
}

export default function ClientAccessGate({ children }: { children: ReactNode }) {
  const { projectSlug } = useParams<{ projectSlug: string }>()
  const { state } = useClientMembership(projectSlug)

  if (state === 'active') {
    return <>{children}</>
  }

  return <ClientClosedScreen copy={COPY[state]} />
}

function ClientClosedScreen({ copy }: { copy: { title: string; body: string } }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--olv-void, #000)',
        padding: 24,
        gap: 14,
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          background: 'rgba(232, 133, 74, 0.08)',
          border: '1px solid rgba(232, 133, 74, 0.30)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 24,
          color: '#E8854A',
          fontFamily: 'var(--font-brand, serif)',
        }}
      >
        ◈
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#edf2ff', maxWidth: 440 }}>{copy.title}</div>
      <div style={{ fontSize: 14, lineHeight: 1.6, color: '#9ca8c0', maxWidth: 420 }}>{copy.body}</div>
    </div>
  )
}
